import { Form } from 'antd';
import { useSearchParams } from 'react-router-dom';

import {
  useConfirmAnonymousPaymentMutation,
  useGetAnonymousPaymentStateQuery,
  YookassaAnonymousPaymentStatus,
} from '@app/api/ClientPayments';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

// Ссылку на оплату формирует бэк: <адрес этой страницы>?t=<одноразовый токен>.
// По токену он сам находит платёж, филиал и организацию, поэтому данных
// платежа в адресе больше нет (us/10744).
const PAYMENT_TOKEN_PARAM = 't';

export type YooKassaPaymentWithEmailViewState =
  | 'noToken'
  | 'loading'
  | 'error'
  | 'succeeded'
  | 'canceled'
  | 'redirecting'
  | 'form';

interface PaymentFormValues {
  email: string;
}

export const useYooKassaPaymentWithEmail = () => {
  const [searchParams] = useSearchParams();
  const paymentToken = searchParams.get(PAYMENT_TOKEN_PARAM) ?? '';
  const { showNotification } = useNotification();

  // Открывая ссылку повторно, клиент мог уже ввести email в прошлый раз —
  // платёж в ЮKassa уже создан, и повторно спрашивать email не нужно, сразу
  // редиректим на его ConfirmationUrl (us/11183).
  const {
    data: paymentState,
    isFetching: isCheckingPaymentState,
    error: paymentStateError,
  } = useGetAnonymousPaymentStateQuery(
    { token: paymentToken },
    { skip: !paymentToken }
  );

  const [confirmAnonymousPayment, { isLoading: isConfirming }] =
    useConfirmAnonymousPaymentMutation();

  const [form] = Form.useForm<PaymentFormValues>();

  const isPaymentAlreadyCreated =
    !!paymentState &&
    paymentState.Status !== YookassaAnonymousPaymentStatus.AwaitingEmail &&
    paymentState.Status !== YookassaAnonymousPaymentStatus.Succeeded &&
    paymentState.Status !== YookassaAnonymousPaymentStatus.Canceled;

  const confirmationUrl = isPaymentAlreadyCreated
    ? paymentState?.ConfirmationUrl ?? null
    : null;

  const viewState: YooKassaPaymentWithEmailViewState = (() => {
    if (!paymentToken) return 'noToken';
    if (isCheckingPaymentState) return 'loading';
    if (paymentStateError) return 'error';
    if (paymentState?.Status === YookassaAnonymousPaymentStatus.Succeeded) {
      return 'succeeded';
    }
    if (paymentState?.Status === YookassaAnonymousPaymentStatus.Canceled) {
      return 'canceled';
    }
    // Платёж уже создан (pending/waiting_for_capture/…), но бэк не отдал
    // ссылку — ждать больше нечего, показываем ошибку вместо вечного спиннера.
    if (isPaymentAlreadyCreated)
      return confirmationUrl ? 'redirecting' : 'error';

    return 'form';
  })();

  const errorMessage = paymentStateError
    ? handleCatchError(paymentStateError)
    : 'Не удалось получить ссылку на оплату';

  const confirmPayment = async ({ email }: PaymentFormValues) => {
    // Без токена подтверждать нечего: бэк по нему определяет платёж.
    if (!paymentToken) return;

    try {
      const { ConfirmationUrl } = await confirmAnonymousPayment({
        Token: paymentToken,
        Email: email.trim(),
      }).unwrap();

      if (!ConfirmationUrl) {
        showNotification('Не удалось получить ссылку на оплату');

        return;
      }

      window.location.assign(ConfirmationUrl);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  return {
    viewState,
    errorMessage,
    confirmationUrl,
    confirmPayment,
    isConfirming,
    form,
  };
};
